import type { ReactNode } from 'react'

/**
 * CanvasOverlay — floating action-item cluster for the canvas corners.
 *
 * Each row splits into THREE visual groups:
 *
 *     [ icon  label ]     ·     [     value ]
 *     └──── group ────┘  sep   └── end ────┘
 *
 * ALIGNMENT CONSTRAINTS (by layout, not runtime math):
 *
 *   1. GROUP (icon + title) is a single unit per row — the icon and its
 *      label read as one lexical piece. Groups all start at the same x
 *      (col 1 left edge) and span the same width (max-content across
 *      rows, so the longest group sets the column).
 *   2. SEPARATOR (`·` middle-dot) lives in its own column between group
 *      and value — so every separator is at the same x, giving the
 *      cluster a clean visual spine.
 *   3. VALUE is right-end-aligned in the flexible last column — every
 *      value's last glyph sits at the same x (cluster's right edge).
 *   4. Icon, label, separator, value all vertically center on the row's
 *      mid-line via align-items:center + leading-none.
 *   5. Uniform spacing: 8px column gap, 4px row gap; 12px cluster inset
 *      from the canvas edge; 56px top when top-anchored (below the 44px
 *      toolbar + 12px gap).
 *
 * Grid layout:
 *
 *     col 1                  col 2  col 3
 *     ┌─────────────────┐    ┌───┐  ┌─────────────────┐
 *     │ icon   label    │... │ · │..│            value│
 *     └─────────────────┘    └───┘  └─────────────────┘
 *       max-content            auto        1fr
 *                                           right-end-aligned
 *
 * Each button uses display:contents so its three children become direct
 * grid children of the outer div, sharing column tracks across all rows.
 */

export interface OverlayRow {
    icon: ReactNode
    iconColor: string
    /** Short word (e.g. `mode`, `theme`). Lowercase, muted. */
    label: string
    /** Current state (e.g. `dark`, `editorial`). Bold. */
    value: string
    valueColor: string
    onClick: () => void
    /** HTML title/tooltip. Defaults to [label]. */
    title?: string
}

export type OverlayPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

interface CanvasOverlayProps {
    position: OverlayPosition
    rows: OverlayRow[]
    /** Optional data-*-trigger token for outside-click handlers in the parent. */
    triggerAttr?: string
}

export function CanvasOverlay({ position, rows, triggerAttr }: CanvasOverlayProps) {
    const positionClass =
        position === 'top-left'      ? 'top-14 left-3'
        : position === 'top-right'    ? 'top-14 right-3'
        : position === 'bottom-left'  ? 'bottom-3 left-3'
        :                               'bottom-3 right-3'
    const triggerProps = triggerAttr ? { [`data-${triggerAttr}-trigger`]: '' } : {}

    return (
        <div {...triggerProps}
            className={`absolute ${positionClass} z-10 grid items-center gap-x-2 gap-y-1`}
            style={{ gridTemplateColumns: 'max-content auto 1fr' }}>
            {rows.map(row => (
                <button key={row.label} onClick={row.onClick} title={row.title ?? row.label}
                    style={{ display: 'contents' }}>
                    {/* col 1 — icon + label as one unit */}
                    <span className="flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
                        <span className="flex items-center justify-center"
                            style={{ width: 20, height: 20, color: row.iconColor }}>
                            {row.icon}
                        </span>
                        <span className="text-xs font-mono leading-none">{row.label}</span>
                    </span>
                    {/* col 2 — separator, shared fixed column → every dot at same x */}
                    <span className="text-xs font-mono leading-none"
                        style={{ color: 'var(--text-muted)', opacity: 0.5 }}>
                        ·
                    </span>
                    {/* col 3 — value, right-end-aligned */}
                    <span className="text-xs font-mono font-bold leading-none"
                        style={{ color: row.valueColor, justifySelf: 'end' }}>
                        {row.value}
                    </span>
                </button>
            ))}
        </div>
    )
}
